import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import {
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
} from 'components/ui/combobox';
import get from 'lodash/get';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FieldValues, Path, SetFieldValue, useWatch, Validate } from 'react-hook-form';
import { groupConsecutiveOptions, OptionGroup, useAutocompleteGroupingV2 } from 'utils/autocompleteGrouping';
import { t } from 'utils/getTranslations';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import { useValidClassifierOptions } from 'utils/useClassifiers.ts';
import ReadonlyField from '../rhf-other/ReadonlyField';
import ChangeReasonInput from './ChangeReasonInput';
import { CommonInputProps, KeyByValue } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useOwnPlaceholder } from './newDesignInput';
import { useMemoizedRules } from './useMemoizedRules';

type GetOptionIdentifier<TOption extends FieldValues> =
    | ((option: TOption) => string | number)
    | KeyByValue<TOption, number | string>;
type GetOptionLabel<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string | null>;

type GetOptionDescription<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string>;

type GetOptionDisabled<TOption extends FieldValues> = ((option: TOption) => boolean) | KeyByValue<TOption, boolean>;

interface Props<TFormData extends FieldValues, TOption extends FieldValues> extends Omit<
    CommonInputProps<TFormData>,
    'validate'
> {
    options: TOption[];
    placeholderOnFocus?: string;
    noPreSelect?: boolean;
    preSelect?: boolean;
    clearable?: boolean;
    onChange?: (value: TOption | null) => void;
    onOpen?: (event: React.SyntheticEvent) => void;
    getOptionLabel: GetOptionLabel<TOption>;
    setValue: SetFieldValue<TFormData>;
    getOptionIdentifier: GetOptionIdentifier<TOption>;
    sortDirection?: 'asc' | 'desc';
    groupSortOrder?: string[];
    defaultGroup?: string;
    noOptionsContent?: React.ReactNode;
    readonly?: boolean | { linkTo: string };
    noReadOnlyDivider?: boolean;
    getOptionDescription?: GetOptionDescription<TOption>;
    getOptionDisabled?: GetOptionDisabled<TOption>;
    validate?: Validate<TOption | null, TFormData> | Record<string, Validate<TOption | null, TFormData>> | undefined;
    requiredFalseWhenNoOptions?: boolean;
    /**
     * How grouped options are separated: by their group title (default), or by a plain rule with no
     * title — for groups that only carry an ordering ("used before" vs the rest), where a title says nothing.
     */
    groupDisplay?: 'titles' | 'divider';
    /** Opt-in: an option's description is drawn as a second line under its label, not only as its hover title. */
    showOptionDescription?: boolean;
    /**
     * Opt-in: the field keeps its value and its focus but offers no other option, and its input is described by the
     * element with this id, which says why.
     */
    lockedReasonId?: string;
}

const SelectInput = <TFormData extends FieldValues, TOption extends FieldValues>({
    name,
    label,
    gap,
    shouldTranslateLabel = true,
    tooltip,
    required,
    requiredFalseWhenNoOptions = false,
    rightLabelComponent,
    bottomRightLabelComponent,
    disabled,
    placeholder,
    shouldTranslatePlaceholder = true,
    placeholderOnFocus,
    wrapperClassName,
    control,
    options,
    noPreSelect,
    preSelect,
    clearable,
    onChange,
    onOpen,
    getOptionLabel,
    setValue,
    getOptionIdentifier,
    sortDirection,
    groupSortOrder,
    defaultGroup,
    validate,
    noOptionsContent,
    readonly = false,
    noReadOnlyDivider,
    getOptionDescription,
    getOptionDisabled,
    withChangeReason = false,
    groupDisplay = 'titles',
    showOptionDescription = false,
    lockedReasonId,
}: Props<TFormData, TOption>) => {
    const watchedValue = useWatch({ control, name });
    const reasonFieldName = `changeReasons.${name}` as Path<TFormData>;

    const validOptions = useValidClassifierOptions(options, watchedValue);

    const initialPlaceholder = useOwnPlaceholder(placeholder, label, shouldTranslatePlaceholder) ?? t('select');
    const [controlledPlaceholder, setControlledPlaceholder] = useState<string>(initialPlaceholder);
    const shouldPreSelectFirstValue = (required && validOptions.length === 1 && !noPreSelect) || preSelect;
    const firstValue = shouldPreSelectFirstValue ? validOptions.at(0) : watchedValue;
    const rules = useMemoizedRules({ required, validate, requiredMessageId: 'validation.choose-option' });
    const fallbackTranslation = defaultGroup ?? t('field.other');

    const isRequired = required && (!requiredFalseWhenNoOptions || validOptions?.length > 0);
    const isEmpty = watchedValue === undefined || watchedValue === null;

    const { hasGroupedOptions, optionsMaybeSorted } = useAutocompleteGroupingV2(validOptions, groupSortOrder);
    // ponytail: LAST_NAME_FIRST flips options carrying first+last (person/employee) to "Last First".
    const lastNameFirst = useLastNameFirst();

    const getLabel = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionLabel === 'string') {
            return optionDisplayLabel(option, lastNameFirst, get(option, getOptionLabel));
        } else {
            return getOptionLabel(option);
        }
    };

    const getValue = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionIdentifier === 'string') {
            return get(option, getOptionIdentifier);
        } else {
            return getOptionIdentifier(option);
        }
    };

    const getDescription = (option: TOption | null) => {
        if (!option) {
            return '';
        }
        if (!getOptionDescription) {
            return '';
        }

        if (typeof getOptionDescription === 'string') {
            return get(option, getOptionDescription);
        } else {
            return getOptionDescription(option);
        }
    };

    const getDisabled = useCallback(
        (option: TOption | null): boolean => {
            if (!option || getOptionDisabled === undefined) {
                return false;
            }
            if (typeof getOptionDisabled === 'string') {
                return get(option, getOptionDisabled);
            } else {
                return getOptionDisabled(option);
            }
        },
        [getOptionDisabled]
    );

    const getSortedOptions = (options: TOption[], direction: 'asc' | 'desc' | undefined) => {
        if (direction === undefined) {
            return options;
        }

        return options.toSorted((a, b) => {
            if (direction === 'asc') {
                return String(getLabel(a)).localeCompare(String(getLabel(b)));
            } else {
                return String(getLabel(b)).localeCompare(String(getLabel(a)));
            }
        });
    };

    // TODO(react-doctor: no-prop-callback-in-effect): `onChange` is the consumer's
    // RHF Controller-style callback and `setValue` is RHF's setter — both are
    // framework APIs for syncing form state when a single option auto-selects.
    // Moving this into a render-phase computation would loop. Intentionally kept.
    // preselects the first value if the field is required, has only ONE option & doesn't have "noPreSelect: true" set
    useEffect(() => {
        const isFirstValueDisabled = firstValue && getOptionDisabled ? getDisabled(firstValue) : false;
        if (!watchedValue && shouldPreSelectFirstValue && firstValue && !isFirstValueDisabled) {
            if (onChange) {
                onChange(firstValue);
            }
            setValue(name, firstValue);
        }
    }, [name, onChange, setValue, watchedValue, shouldPreSelectFirstValue, firstValue, getDisabled, getOptionDisabled]);

    const flatOptions = hasGroupedOptions ? optionsMaybeSorted : getSortedOptions(validOptions, sortDirection);

    // `useAutocompleteGroupingV2` has already ordered the options so every group's rows sit together.
    const groupedOptions = useMemo<OptionGroup<TOption>[]>(() => {
        if (!hasGroupedOptions) {
            return [];
        }
        return groupConsecutiveOptions(optionsMaybeSorted, (option) => String(option.group ?? fallbackTranslation));
    }, [hasGroupedOptions, optionsMaybeSorted, fallbackTranslation]);

    const getSameNameEmail = useSameNameEmail(validOptions);

    const renderOptionRow = (option: TOption) => {
        const label = (
            <PersonOptionLabel option={option} label={getLabel(option)} sameNameEmail={getSameNameEmail(option)} />
        );
        const descriptionLine = showOptionDescription ? getDescription(option) : '';
        return (
            <ComboboxItem
                key={getValue(option)}
                value={option}
                className={descriptionLine ? 'items-start' : undefined}
                disabled={getOptionDisabled ? getDisabled(option) : undefined}
                // The row's accessible name comes from its own content; `title` is the supplementary channel — a
                // native hover bubble, and the accessible description, which is what the accname spec falls back
                // to once a name comes from elsewhere. A description the row does not draw wins it; otherwise the
                // bubble shows the whole label of a name the row cuts off, and a drawn description is not said twice.
                title={
                    (!descriptionLine && getOptionDescription ? getDescription(option) : undefined) ||
                    getLabel(option) ||
                    undefined
                }
            >
                {descriptionLine ? (
                    <span className="flex flex-col gap-0.5">
                        {label}
                        <span className="text-muted-foreground text-xs leading-4">{descriptionLine}</span>
                    </span>
                ) : (
                    label
                )}
            </ComboboxItem>
        );
    };

    return (
        <>
            <Controller
                control={control}
                name={name}
                rules={rules}
                render={({ field: { ref, onChange: innerOnChange, onBlur, value }, fieldState: { error } }) => (
                    <InputWrapper
                        name={name}
                        label={label}
                        shouldTranslateLabel={shouldTranslateLabel}
                        tooltip={tooltip}
                        required={isRequired}
                        isEmpty={isEmpty}
                        readonly={!!readonly}
                        rightLabelComponent={rightLabelComponent}
                        bottomRightLabelComponent={bottomRightLabelComponent}
                        hasErrors={!!error}
                        error={readonly ? undefined : error}
                        className={wrapperClassName}
                        gap={gap}
                        noReadOnlyDivider={noReadOnlyDivider}
                    >
                        {readonly ? (
                            <ReadonlyField
                                value={value}
                                getLabel={getLabel}
                                getValue={getValue}
                                linkTo={typeof readonly === 'object' ? readonly?.linkTo : undefined}
                            />
                        ) : (
                            <Combobox<TOption>
                                selectionAsPlaceholder
                                items={(hasGroupedOptions ? groupedOptions : flatOptions) as readonly TOption[]}
                                value={value ?? null}
                                disabled={disabled}
                                readOnly={!!lockedReasonId}
                                // The same label the rows render, so the selected text and the type-ahead
                                // filter agree with the list under the LAST_NAME_FIRST flip.
                                itemToStringLabel={(option) => String(getLabel(option) ?? '')}
                                isItemEqualToValue={(option, selectedOption) =>
                                    getValue(option) === getValue(selectedOption)
                                }
                                onOpenChange={(open, details) => {
                                    if (open && onOpen) {
                                        onOpen(details.event as unknown as React.SyntheticEvent);
                                    }
                                }}
                                onValueChange={(data) => {
                                    innerOnChange(data);
                                    if (onChange) {
                                        onChange(data);
                                    }
                                }}
                            >
                                <ComboboxInput
                                    id={name}
                                    name={name}
                                    ref={ref}
                                    className="bg-paper"
                                    title={(value && getLabel(value)) || undefined}
                                    aria-invalid={!!error}
                                    aria-describedby={lockedReasonId}
                                    required={isRequired ?? false}
                                    disabled={disabled}
                                    showClear={
                                        !lockedReasonId &&
                                        !((clearable !== undefined && !clearable) || shouldPreSelectFirstValue)
                                    }
                                    placeholder={controlledPlaceholder}
                                    data-form-type="other"
                                    data-lpignore="true"
                                    onFocus={() => {
                                        if (placeholderOnFocus) {
                                            setControlledPlaceholder(t(placeholderOnFocus));
                                        }
                                    }}
                                    onBlur={() => {
                                        onBlur();
                                        if (placeholderOnFocus) {
                                            setControlledPlaceholder(initialPlaceholder);
                                        }
                                    }}
                                />
                                <ComboboxContent className="shadow-dropdown">
                                    <ComboboxEmpty>{noOptionsContent ?? t('no-options')}</ComboboxEmpty>
                                    {/* The list's function child receives what the filter left, so a
                                        group whose every row was filtered out never renders — and the
                                        titleless variant's rule, drawn on every group but the first one
                                        present, lands above the right ones. */}
                                    <ComboboxList>
                                        {hasGroupedOptions
                                            ? (group: OptionGroup<TOption>) => (
                                                  <ComboboxGroup
                                                      key={group.key}
                                                      items={group.items}
                                                      className={
                                                          groupDisplay === 'divider'
                                                              ? 'border-border not-first:mt-1 not-first:border-t not-first:pt-1'
                                                              : undefined
                                                      }
                                                  >
                                                      {groupDisplay === 'titles' ? (
                                                          <ComboboxLabel>{group.value}</ComboboxLabel>
                                                      ) : null}
                                                      <ComboboxCollection>
                                                          {(option: TOption) => renderOptionRow(option)}
                                                      </ComboboxCollection>
                                                  </ComboboxGroup>
                                              )
                                            : (option: TOption) => renderOptionRow(option)}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                        )}
                    </InputWrapper>
                )}
            />
            {withChangeReason ? <ChangeReasonInput control={control} name={reasonFieldName} required={true} /> : null}
        </>
    );
};

export default SelectInput;
